<template>
  <main class="min-h-screen bg-gray-300">
    <section class="flex flex-col justify-center min-h-screen">
      <div class="w-full max-w-xl mx-auto sm:px-6 lg:px-8">
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="border-b border-gray-200 px-4 py-5 sm:px-6 font-medium">
            {{ $t('invoices_export_title') }}
          </div>
          <div class="px-4 py-5 sm:p-6">
            <div class="grid md:grid-cols-2 gap-x-6 gap-y-8">
              <sqr-input-date
                :label="$t('invoices_export_from')"
                v-model="from"
              />
              <sqr-input-date
                :label="$t('invoices_export_until')"
                v-model="until"
              />
              <sqr-input-text
                :label="$t('invoices_export_debtor')"
                v-model="debtor"
              />
              <sqr-input-text
                :label="$t('invoices_export_creditor')"
                v-model="creditor"
              />
              <sqr-input-radio
                class="col-span-2"
                :label="$t('invoices_export_entry_filter')"
                v-model="entryFilter"
                :options="entryFilterOptions"
                box-class="flex flex-col"
                item-class="mb-2 last-child:mb-0"
              />
            </div>
          </div>
          <sqr-error-text class="field" :error="exportError" />
          <div
            class="border-t border-gray-200 px-4 py-4 sm:px-6 flex space-x-2 justify-end bg-gray-50"
          >
            <sqr-button
              :label="$t('buttons_cancel')"
              @click="$router.back()"
              color="transparent"
            />
            <sqr-button
              :label="$t('invoices_export_export')"
              @click="process()"
              color="green"
              :loading="exporting"
            />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import zipcelx from 'zipcelx';
import SqrInputDate from '@/components/SqrInputDate';
import SqrInputText from '@/components/SqrInputText';
import SqrInputRadio from '@/components/SqrInputRadio';
import SqrErrorText from '@/components/SqrErrorText';
import SqrButton from '@/components/SqrButton';

import date from '@/filters/date';
import patient from '@/filters/patient';
import amountValue from '@/filters/amountValue';
import currency from '@/filters/currency';

import { subYears, startOfYear, endOfYear, format } from 'date-fns';

export default {
  name: 'InvoicesExport',
  components: {
    SqrInputDate,
    SqrInputText,
    SqrInputRadio,
    SqrErrorText,
    SqrButton
  },
  computed: {
    ...mapGetters('auth', ['uid']),
    entryFilterOptions() {
      return [
        { id: 'all', label: this.$t('invoices_export_entry_filter_all') },
        {
          id: 'deposit',
          label: this.$t('invoices_export_entry_filter_deposit')
        },
        {
          id: 'no-deposit',
          label: this.$t('invoices_export_entry_filter_no_deposit')
        }
      ];
    }
  },
  data() {
    const from = format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd');
    const until = format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd');
    return {
      from,
      until,
      debtor: '',
      creditor: '',
      entryFilter: 'all',
      exporting: false,
      exportError: null
    };
  },
  mounted() {
    this.$a().logEvent('invoice_export');
  },
  methods: {
    async process() {
      try {
        this.exporting = true;
        this.exportError = null;

        const res = await this.$db()
          .collection('accounts')
          .doc(this.uid)
          .collection('invoices')
          .where('date', '>=', this.from)
          .where('date', '<=', this.until)
          .orderBy('date', 'asc')
          .get();

        const raw = res.docs.map(doc => doc.data());

        let filtered = raw;
        if (this.entryFilter === 'deposit') {
          filtered = filtered.filter(invoice => invoice.deposit?.amount);
        } else if (this.entryFilter === 'no-deposit') {
          filtered = filtered.filter(invoice => !invoice.deposit?.amount);
        }

        const rows = filtered.map(invoice => [
          { value: date(invoice.date) || '', type: 'string' },
          { value: invoice.number ?? '', type: 'string' },
          { value: patient(invoice.patient), type: 'string' },
          { value: this.debtor ?? '', type: 'string' },
          { value: this.creditor ?? '', type: 'string' },
          { value: amountValue(invoice.totalTi) ?? 0, type: 'number' },
          { value: amountValue(invoice.deposit) ?? 0, type: 'number' },
          { value: currency(invoice.totalTi) ?? '', type: 'string' }
        ]);

        const config = {
          filename: 'Simple590-export',
          sheet: {
            data: [
              [
                { value: 'Date', type: 'string' },
                { value: 'Numéro', type: 'string' },
                { value: 'Client', type: 'string' },
                { value: 'Débiteur', type: 'string' },
                { value: 'Créditeur', type: 'string' },
                { value: 'Montant', type: 'string' },
                { value: 'Accompte', type: 'string' },
                { value: 'Devise', type: 'string' }
              ],
              ...rows,
              [],
              [
                { value: 'Exporté par Simple590 https://www.simple590.ch/', type: 'string' },
              ]
            ]
          }
        };

        zipcelx(config);
        this.$router.back();
      } catch (error) {
        this.exportError = error;
        return Promise.reject(error);
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>
